import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { addSentryBreadcrumb } from '../../shared/sentry/sentryUser';
export const CATALOG_LAST_PULL_AT_KEY = 'sync.catalog_last_pull_at';
export const CUSTOMER_LAST_PULL_AT_KEY = 'sync.fiado_last_pull_at';
export const EXPENSES_LAST_PULL_AT_KEY = 'sync.expenses_last_pull_at';
export const PURCHASES_LAST_PULL_AT_KEY = 'sync.purchases_last_pull_at';
export const HOSPITALITY_LAST_PULL_AT_KEY = 'sync.hospitality_last_pull_at';
const LAST_SUCCESSFUL_SYNC_AT_KEY = 'sync.last_successful_sync_at';

const memoryCache = new Map<string, string>();

/** Prefijo para distinguir las keys de sync timestamps en storage. */
const STORAGE_PREFIX = 'vuelto_sync_ts_';

function storageKey(key: string): string {
  return STORAGE_PREFIX + key;
}

/** Persiste el timestamp al storage subyacente (localStorage web / AsyncStorage nativo). */
function persistToStorage(key: string, iso: string): void {
  if (Platform.OS === 'web' && typeof localStorage !== 'undefined') {
    try {
      localStorage.setItem(storageKey(key), iso);
    } catch (error) {
      // localStorage puede estar lleno o deshabilitado (modo privado). Best-effort.
      addSentryBreadcrumb('sync', 'localStorage.setItem failed (private mode/quota?)', 'warning', { key, error: String(error) });
    }
    return;
  }
  // Nativo: AsyncStorage es async, fire-and-forget.
  void AsyncStorage.setItem(storageKey(key), iso).catch((error) => {
    // Best-effort: si falla, el memory cache alcanza para la sesión.
    addSentryBreadcrumb('sync', 'AsyncStorage.setItem failed', 'warning', { key, error: String(error) });
  });
}

/** Lee un timestamp del storage subyacente (async). */
async function readFromStorage(key: string): Promise<string | null> {
  if (Platform.OS === 'web' && typeof localStorage !== 'undefined') {
    try {
      return localStorage.getItem(storageKey(key));
    } catch {
      return null;
    }
  }
  try {
    return await AsyncStorage.getItem(storageKey(key));
  } catch {
    return null;
  }
}

function setSyncTimestampMemory(key: string, iso: string): void {
  memoryCache.set(key, iso);
  persistToStorage(key, iso);
}

/** Timestamp de pull en memoria + storage (ram-only). */
export function getSyncTimestampMemory(key: string): string | null {
  return memoryCache.get(key) ?? null;
}

/**
 * Hidrata el memory cache desde storage (AsyncStorage/localStorage) al boot.
 * Llamar una vez antes del primer pull de catálogo.
 */
export async function hydrateSyncTimestampsFromStorage(): Promise<void> {
  const keys = [
    CATALOG_LAST_PULL_AT_KEY,
    CUSTOMER_LAST_PULL_AT_KEY,
    EXPENSES_LAST_PULL_AT_KEY,
    PURCHASES_LAST_PULL_AT_KEY,
    HOSPITALITY_LAST_PULL_AT_KEY,
    LAST_SUCCESSFUL_SYNC_AT_KEY,
  ];
  await Promise.all(
    keys.map(async (key) => {
      const value = await readFromStorage(key);
      if (value) memoryCache.set(key, value);
    }),
  );
}

function mergeSyncTimestampsWithMemory<T extends Record<string, string | null>>(
  keys: Record<keyof T, string>,
  fromDb: T
): T {
  const merged = { ...fromDb };
  for (const field of Object.keys(keys) as (keyof T)[]) {
    const mem = memoryCache.get(keys[field]);
    if (mem) {
      merged[field] = mem as T[keyof T];
    }
  }
  return merged;
}

export interface SyncTimestamps {
  catalogLastPullAt: string | null;
  customerLastPullAt: string | null;
  expensesLastPullAt: string | null;
  purchasesLastPullAt: string | null;
  hospitalityLastPullAt: string | null;
  lastSuccessfulSyncAt: string | null;
}

const MEMORY_KEY_MAP = {
  catalogLastPullAt: CATALOG_LAST_PULL_AT_KEY,
  customerLastPullAt: CUSTOMER_LAST_PULL_AT_KEY,
  expensesLastPullAt: EXPENSES_LAST_PULL_AT_KEY,
  purchasesLastPullAt: PURCHASES_LAST_PULL_AT_KEY,
  hospitalityLastPullAt: HOSPITALITY_LAST_PULL_AT_KEY,
  lastSuccessfulSyncAt: LAST_SUCCESSFUL_SYNC_AT_KEY,
} as const;

/**
 * Timestamps de bajada (catálogo, customer) y última subida exitosa.
 * Ram-only: lee del memory cache (hidratado al boot desde storage).
 * Legacy: lee de SQLite app_settings (ya no aplica — stub no-op).
 */
export const getSyncTimestamps = async (): Promise<SyncTimestamps> => {
  const fromMemory: SyncTimestamps = {
    catalogLastPullAt: getSyncTimestampMemory(CATALOG_LAST_PULL_AT_KEY),
    customerLastPullAt: getSyncTimestampMemory(CUSTOMER_LAST_PULL_AT_KEY),
    expensesLastPullAt: getSyncTimestampMemory(EXPENSES_LAST_PULL_AT_KEY),
    purchasesLastPullAt: getSyncTimestampMemory(PURCHASES_LAST_PULL_AT_KEY),
    hospitalityLastPullAt: getSyncTimestampMemory(HOSPITALITY_LAST_PULL_AT_KEY),
    lastSuccessfulSyncAt: getSyncTimestampMemory(LAST_SUCCESSFUL_SYNC_AT_KEY),
  };

  return fromMemory;
};

export function recordSyncPullTimestamp(key: string, iso?: string): void {
  const value = iso ?? new Date().toISOString();
  setSyncTimestampMemory(key, value);
}
